import React from 'react';
import {
    Alert,
    Button,
    Card,
    Col,
    Input,
    Row,
    Space,
    Statistic,
    Tag,
    Typography,
} from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';

const { Text } = Typography;
const { TextArea } = Input;

export interface GatePrompt {
    id: string;
    template: string;
}

export interface GatePersona {
    id: string;
    preamble: string;
}

export function parseJsonList<T>(raw: string | undefined): T[] {
    if (!raw) return [];
    try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? (parsed as T[]) : [];
    } catch {
        return [];
    }
}

interface Props {
    prompts: GatePrompt[];
    personas: GatePersona[];
    onPromptsChange: (next: GatePrompt[]) => void;
    onPersonasChange: (next: GatePersona[]) => void;
    // prompts x personas x models x modes x replicates is a third of a run's
    // calls, and a prompt added here multiplies against everything else.
    gateN: number;
    panelSize: number;
    memoryPass: boolean;
}

// A brand that is never listed never has its drivers evaluated, so the gate
// leads. Its questions ask about a CATEGORY and must never name the brand: a
// model handed the name lists the name, and inclusion reads near 100%.
export const ModelBeliefsGateEditor: React.FC<Props> = ({
    prompts,
    personas,
    onPromptsChange,
    onPersonasChange,
    gateN,
    panelSize,
    memoryPass,
}) => {
    const calls =
        prompts.length *
        personas.length *
        Math.max(panelSize, 0) *
        (memoryPass ? 2 : 1) *
        Math.max(gateN, 0);

    const missingCategory = prompts.filter(
        (p) => !(p.template ?? '').includes('{category}') && !(p.template ?? '').includes('{products}'),
    );

    return (
        <Card size="small" title="Gate battery" style={{ marginBottom: 16 }}>
            <Row gutter={16} style={{ marginBottom: 12 }}>
                <Col span={8}>
                    <Statistic
                        title="Gate calls per run"
                        value={calls}
                        valueStyle={{ fontSize: 20 }}
                    />
                    <Text type="secondary" style={{ fontSize: 11 }}>
                        {prompts.length} prompt{prompts.length === 1 ? '' : 's'} ×{' '}
                        {personas.length} persona{personas.length === 1 ? '' : 's'} ×{' '}
                        {panelSize} model{panelSize === 1 ? '' : 's'} ×{' '}
                        {memoryPass ? '2 modes' : '1 mode'} × {gateN} replicates. Adding
                        one prompt multiplies against every other dimension.
                    </Text>
                </Col>
                <Col span={16}>
                    <Alert
                        type="warning"
                        showIcon
                        message="Nothing here may name the brand"
                        description="Every rendered question is leak-checked against the brand name, its domains and its competitors before the call. A name in a question skips that question; a name in the system prompt fails the gate for the whole run. {category} comes from brand config, so a category label carrying the brand leaks without any template being touched."
                    />
                </Col>
            </Row>

            <Space direction="vertical" size={4} style={{ width: '100%' }}>
                <Space>
                    <Text strong style={{ fontSize: 13 }}>
                        Questions
                    </Text>
                    <Button
                        size="small"
                        icon={<PlusOutlined style={{ marginRight: 4 }} />}
                        onClick={() =>
                            onPromptsChange([...prompts, { id: '', template: '' }])
                        }
                    >
                        Add
                    </Button>
                </Space>
                {prompts.map((prompt, index) => (
                    <Row key={index} gutter={8} align="middle">
                        <Col xs={24} lg={5}>
                            <Input
                                size="small"
                                value={prompt.id}
                                placeholder="recommend"
                                onChange={(e) =>
                                    onPromptsChange(
                                        prompts.map((p, i) =>
                                            i === index ? { ...p, id: e.target.value } : p,
                                        ),
                                    )
                                }
                            />
                        </Col>
                        <Col xs={22} lg={18}>
                            <Input
                                size="small"
                                value={prompt.template}
                                placeholder="Which {category} brands would you recommend{market}?"
                                onChange={(e) =>
                                    onPromptsChange(
                                        prompts.map((p, i) =>
                                            i === index
                                                ? { ...p, template: e.target.value }
                                                : p,
                                        ),
                                    )
                                }
                            />
                        </Col>
                        <Col xs={2} lg={1}>
                            <Button
                                size="small"
                                type="text"
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() =>
                                    onPromptsChange(prompts.filter((_p, i) => i !== index))
                                }
                            />
                        </Col>
                    </Row>
                ))}
                <Text type="secondary" style={{ fontSize: 11 }}>
                    <code>{'{category}'}</code> and <code>{'{products}'}</code> come from
                    brand config, <code>{'{market}'}</code> from the run&rsquo;s country.
                    A question is asked as a shopper would ask it.
                </Text>
                {missingCategory.length > 0 && (
                    <Alert
                        type="warning"
                        showIcon
                        message={`${missingCategory.length} question${
                            missingCategory.length === 1 ? '' : 's'
                        } name${missingCategory.length === 1 ? 's' : ''} no category`}
                        description="A question with neither {category} nor {products} asks about nothing in particular, and what it measures is not this brand's market."
                    />
                )}
            </Space>

            <Space
                direction="vertical"
                size={4}
                style={{ width: '100%', marginTop: 16 }}
            >
                <Space>
                    <Text strong style={{ fontSize: 13 }}>
                        Shoppers
                    </Text>
                    <Tag>{personas.length}</Tag>
                    <Button
                        size="small"
                        icon={<PlusOutlined style={{ marginRight: 4 }} />}
                        onClick={() =>
                            onPersonasChange([...personas, { id: '', preamble: '' }])
                        }
                    >
                        Add
                    </Button>
                </Space>
                {personas.map((persona, index) => (
                    <Row key={index} gutter={8} align="middle">
                        <Col xs={24} lg={5}>
                            <Input
                                size="small"
                                value={persona.id}
                                placeholder="first_time"
                                onChange={(e) =>
                                    onPersonasChange(
                                        personas.map((p, i) =>
                                            i === index ? { ...p, id: e.target.value } : p,
                                        ),
                                    )
                                }
                            />
                        </Col>
                        <Col xs={22} lg={18}>
                            <TextArea
                                size="small"
                                rows={2}
                                value={persona.preamble}
                                placeholder="You are helping someone buying in this category for the first time."
                                onChange={(e) =>
                                    onPersonasChange(
                                        personas.map((p, i) =>
                                            i === index
                                                ? { ...p, preamble: e.target.value }
                                                : p,
                                        ),
                                    )
                                }
                            />
                        </Col>
                        <Col xs={2} lg={1}>
                            <Button
                                size="small"
                                type="text"
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() =>
                                    onPersonasChange(
                                        personas.filter((_p, i) => i !== index),
                                    )
                                }
                            />
                        </Col>
                    </Row>
                ))}
                <Text type="secondary" style={{ fontSize: 11 }}>
                    Who the model thinks it is helping. A first-time buyer and a repeat
                    buyer get different lists, and the difference is part of the reading.
                </Text>
            </Space>
        </Card>
    );
};
